import React from 'react';
import Imgix from 'react-imgix';
import { css, SerializedStyles } from '@emotion/react';
import { MediaRefFragment } from '$/GraphQL/schema/types';
import { getCssImagePreviewByCrop, getOriginalSizeImage } from '$/utils/image';
import { isFromDomain } from '$/utils/string';

type ImgixSafeProps = Omit<React.ComponentProps<typeof Imgix>, 'src'> & {
  refValue?: MediaRefFragment | null;
  css?: SerializedStyles;
  src?: string;
};

const ImgixSafe: React.FC<ImgixSafeProps> = (props) => {
  const isExternalUrl =
    props.refValue?.imageUrl &&
    !isFromDomain(props.refValue?.imageUrl, 'imgix.net') &&
    !isFromDomain(props.refValue?.imageUrl, 'unsplash.com');
  const isDataImage =
    props.src?.startsWith('data:') ||
    props.refValue?.imageUrl?.startsWith('data:') ||
    (props.refValue?.crop && isExternalUrl);

  const [originalDimensions, setOriginalDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  React.useEffect(() => {
    if (isDataImage) {
      const src = props.src ?? props.refValue?.imageUrl;
      if (src) {
        getOriginalSizeImage(src).then(setOriginalDimensions);
      }
    }
  }, [isDataImage, props.src, props.refValue?.imageUrl]);

  if (isDataImage) {
    const { imgixParams, htmlAttributes, css: imgixCss, className, src: srcProp } = props;

    const src = srcProp ?? props.refValue?.imageUrl;

    // Look at width/height from imgixParams otherwise props width/height - take the min if both exist
    let width =
      imgixParams?.w && props.width
        ? Math.min(Number(imgixParams.w), Number(props.width))
        : imgixParams?.w
          ? Number(imgixParams.w)
          : props.width;
    let height =
      imgixParams?.h && props.height
        ? Math.min(Number(imgixParams.h), Number(props.height))
        : imgixParams?.h
          ? Number(imgixParams.h)
          : props.height;
    const aspectRatio = Number(originalDimensions?.width) / (originalDimensions?.height ?? 1);

    // If !width or !height, calculate the other based on the aspect ratio of the original image
    if (!width && height) {
      width = height * aspectRatio;
    } else if (!height && width) {
      height = width / aspectRatio;
    }

    // Convert Imgix params to CSS
    const containerCss = css`
      ${imgixCss}
      ${width ? `width: ${width}px !important;` : ''}
      ${height ? `height: ${height}px !important;` : ''}
      ${imgixParams?.mask === 'ellipse' ? 'border-radius: 50%;' : ''}
      ${imgixParams?.q && typeof imgixParams.q === 'number' && imgixParams.q >= 80
        ? 'image-rendering: crisp-edges;'
        : ''}
      background-image: url(${src});
      background-size: ${imgixParams?.fit === 'crop' ? 'cover' : ''}
        ${imgixParams?.fit === 'contain' ? 'contain' : ''}
        ${imgixParams?.fit === 'fill' ? '100% 100%' : ''}
        ${imgixParams?.fit === 'scale' ? 'scale-down' : ''}
        ${imgixParams?.fit === 'clamp' ? 'none' : ''};
      background-repeat: no-repeat;
      ${props.refValue?.crop ? getCssImagePreviewByCrop(props.refValue) : ''}
    `;

    return (
      <div
        className={className}
        css={containerCss}
        {...(htmlAttributes as React.HTMLAttributes<HTMLDivElement>)}
      />
    );
  }

  return (
    <Imgix
      {...props}
      src={props.src ?? props.refValue!.imageUrl}
      sizes={`${props.imgixParams?.w}px`}
      css={css`
        width: ${props.imgixParams?.w}px;
        height: ${props.imgixParams?.h}px;
      `}
    />
  );
};

export default ImgixSafe;
